import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {Panel, PanelHeader} from '@Components/panel';
import {updatePanelProperties, updatePanelSizeData} from '@Components/panel/panel-reducer';
import {ColorPickerMobile, ColorPickerMobileSection} from '@Components/color-picker-mobile';
import type {FillObject} from '@PosterWhiteboard/classes/fill.class';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import type {RGBA} from '@Utils/color.util';
import {getRGBAArrayFromColorStorage} from '@Utils/color.util';
import {getColorFillTypeFromFillType, getColorObjectArrayFromFillColor} from '@Utils/color.util';
import {onAddBrandAssetClickedHandler} from '@Components/poster-editor/library/poster-editor-library';
import {useAppDispatch, useAppSelector} from '@/hooks';
import styles from './poster-editor-color-panel.module.scss';

interface PosterEditorColorPanelProps {
  panelId: string;
  fillObject: FillObject;
  initialSelectedColorIndex?: number;
  showInitialBackButton?: boolean;
  headerText?: string;
  isAlphaDisabled?: boolean;

  onChangeColor(fillColor: RGBA[], undoable?: boolean): void;

  onBackButton?(): void;

  onClose(): void;
}

export default function PosterEditorColorPanel({
  isAlphaDisabled = false,
  headerText = '',
  onBackButton = (): void => {},
  showInitialBackButton = true,
  initialSelectedColorIndex = 0,
  ...props
}: PosterEditorColorPanelProps): ReactElement {
  const [currentlySelectedPanel, setCurrentlySelectedPanel] = useState(ColorPickerMobileSection.SWATCHES);
  const [selectedColorIndex, setSelectedColorIndex] = useState(initialSelectedColorIndex);
  const dispatch = useAppDispatch();
  const activeBrandId: string = useAppSelector((state) => {
    return state.brands.activeBrandId ?? '';
  });
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect((): void => {
    updatePanelSnapPointsInRedux(currentlySelectedPanel);
  }, [activeBrandId]);

  const getHeightForPanel = (): string => {
    if (currentlySelectedPanel === ColorPickerMobileSection.SWATCHES) {
      return '250px';
    }

    if (props.fillObject.fillType === FillTypes.SOLID) {
      return '292px';
    }

    return '384px';
  };

  const getSecondSnapPoint = (): number => {
    if (!containerRef?.current) {
      return 0;
    }

    let height = 82;
    const {children} = containerRef.current;
    for (let i = 0; i < children.length; i++) {
      height += children[i].clientHeight;
    }

    return height;
  };

  const setSnapPoints = (): void => {
    dispatch(
      updatePanelProperties({
        panelId: props.panelId,
        hasSnapPoints: true,
        snapPointsForBottomSheet: [250, getSecondSnapPoint()],
      })
    );
  };

  const disableSnapPoints = (): void => {
    dispatch(
      updatePanelProperties({
        panelId: props.panelId,
        hasSnapPoints: false,
        snapPointsForBottomSheet: undefined,
      })
    );
  };

  const updatePanelSnapPointsInRedux = (currentSection: ColorPickerMobileSection): void => {
    if (currentSection === ColorPickerMobileSection.SWATCHES) {
      setSnapPoints();
    } else {
      disableSnapPoints();
    }
  };

  useEffect((): void => {
    dispatch(
      updatePanelSizeData({
        sizeDataId: props.panelId,
        sizeData: {
          height: getHeightForPanel(),
        },
      })
    );
  }, [currentlySelectedPanel]);

  const getHeadingText = (): string => {
    if (currentlySelectedPanel === ColorPickerMobileSection.HEXPICKER) {
      return window.i18next.t('pmwjs_picker');
    }

    if (headerText) {
      return headerText;
    }

    if (props.fillObject.fillType === FillTypes.SOLID) {
      return window.i18next.t('pmwjs_color');
    }

    if (selectedColorIndex === 0) {
      return window.i18next.t('pmwjs_color_one');
    }

    return window.i18next.t('pmwjs_color_two');
  };

  const onBackButtonCallback = (): void => {
    if (currentlySelectedPanel === ColorPickerMobileSection.SWATCHES) {
      disableSnapPoints();
      onBackButton();
    }

    if (currentlySelectedPanel === ColorPickerMobileSection.HEXPICKER) {
      setCurrentlySelectedPanel(ColorPickerMobileSection.SWATCHES);
    }
  };

  const onCloseCallback = (): void => {
    props.onClose();
  };

  const showBackButtonCondition = (): boolean => {
    return !(currentlySelectedPanel === ColorPickerMobileSection.SWATCHES && !showInitialBackButton);
  };

  const getHeader = (): ReactElement => {
    return <PanelHeader showBackButton={showBackButtonCondition()} onBackButton={onBackButtonCallback} onClose={onCloseCallback} title={getHeadingText()} />;
  };

  const getColorsOnMyDesign = (): string[] => {
    return window.posterEditor?.whiteboard?.getCurrentPage().getColorsOnPage() ?? [];
  };

  const getColorPickerContent = (): ReactElement => {
    return (
      <ColorPickerMobile
        color={getColorObjectArrayFromFillColor(props.fillObject.fillColor)}
        onMyDesign={getColorsOnMyDesign()}
        type={getColorFillTypeFromFillType(props.fillObject.fillType)}
        onChangeColor={(colorObj): void => {
          props.onChangeColor(getRGBAArrayFromColorStorage(colorObj), false);
        }}
        onChangeEnded={(colorObj): void => {
          props.onChangeColor(getRGBAArrayFromColorStorage(colorObj), true);
        }}
        onToggleSection={setCurrentlySelectedPanel}
        onContentChanged={(newSection): void => {
          updatePanelSnapPointsInRedux(newSection);
        }}
        onAddBrandAssetClicked={(): void => {
          void onAddBrandAssetClickedHandler();
        }}
        onOpenPickingMode={(): void => {}}
        section={currentlySelectedPanel}
        selectedIndex={selectedColorIndex}
        onChangeSelectedIndex={setSelectedColorIndex}
        isAlphaDisabled={isAlphaDisabled}
      />
    );
  };

  const getContent = (): ReactElement => {
    return (
      <div ref={containerRef} className={styles.container}>
        {getColorPickerContent()}
      </div>
    );
  };

  return <Panel panelHeader={getHeader()} panelContent={getContent()} />;
}
